import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

import {
    CREST_HEAD_OFFSET_Y,
    CREST_RING_TEXT,
    crestEars,
    crestEyeLids,
    crestFace,
    crestForelock,
    crestMouth,
    crestNostrils,
    crestPupils,
    crestWool
} from '@/lib/crest'

interface CrestMarkProps {
    className?: string
    label?: string
}

// Flat version of the sheep crest. Used as the fallback when 3D is off and as small marks.
const CrestMark: FC<CrestMarkProps> = ({ className, label }): ReactNode => {
    const id = useId().replace(/:/g, '')
    const ringId = `crest-ring-${id}`
    const clipId = `crest-clip-${id}`

    return (
        <svg
            viewBox='0 0 512 512'
            className={cn('block', className)}
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
        >
            <defs>
                <path id={ringId} d='M 256 50 A 206 206 0 1 1 255.9 50' />
                <clipPath id={clipId}>
                    <circle cx='256' cy='256' r='254' />
                </clipPath>
            </defs>

            <g clipPath={`url(#${clipId})`}>
                <circle cx='256' cy='256' r='254' fill='#0D0D0D' />
                <circle cx='256' cy='256' r='236' fill='none' stroke='#F1EFEA' strokeWidth='3' />
                <circle cx='256' cy='256' r='176' fill='none' stroke='#F1EFEA' strokeWidth='3' />

                <text
                    fill='#F1EFEA'
                    fontSize='21'
                    fontWeight='700'
                    dominantBaseline='middle'
                    style={{ fontFamily: 'var(--font-app-mono, ui-monospace, monospace)' }}
                >
                    <textPath href={`#${ringId}`} textLength='1290' lengthAdjust='spacing'>
                        {CREST_RING_TEXT}
                    </textPath>
                </text>

                <circle cx='256' cy='256' r='168' fill='#F1EFEA' />

                <g transform={`translate(0 ${CREST_HEAD_OFFSET_Y})`}>
                    {crestEars.map(path => (
                        <path key={path} d={path} fill='#0D0D0D' />
                    ))}

                    {crestWool.map(([x, y, r]) => (
                        <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill='#0D0D0D' />
                    ))}

                    <path d={crestFace} fill='#F1EFEA' stroke='#0D0D0D' strokeWidth='7' />

                    {crestForelock.map(([x, y, r]) => (
                        <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill='#0D0D0D' />
                    ))}

                    {crestEyeLids.map(([x1, y1, x2, y2]) => (
                        <line
                            key={`${x1}-${y1}`}
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke='#0D0D0D'
                            strokeWidth='6'
                            strokeLinecap='round'
                        />
                    ))}

                    {crestPupils.map(path => (
                        <path key={path} d={path} fill='#0D0D0D' />
                    ))}

                    {crestNostrils.map(([x, y]) => (
                        <ellipse key={`${x}-${y}`} cx={x} cy={y} rx='5' ry='3.6' fill='#0D0D0D' />
                    ))}

                    <line
                        x1={crestMouth[0]}
                        y1={crestMouth[1]}
                        x2={crestMouth[2]}
                        y2={crestMouth[3]}
                        stroke='#0D0D0D'
                        strokeWidth='5'
                        strokeLinecap='round'
                    />
                </g>
            </g>
        </svg>
    )
}

export default CrestMark
